import type { useUploadSpeed } from '~/composables/useUploadSpeed'

export type UploadErrorPayload = {
    message?: string
    statusMessage?: string
}

/** Why an upload request failed. `http` carries the response status and its parsed error body. */
export class UploadRequestError extends Error {
    constructor(
        readonly kind: 'http' | 'network' | 'aborted',
        readonly status?: number,
        readonly payload?: UploadErrorPayload,
    ) {
        super(payload?.statusMessage || payload?.message || `Upload failed (${ kind }${ status ? ` ${ status }` : '' })`)
    }
}

export const parseUploadErrorPayload = (value: unknown): UploadErrorPayload | undefined => {
    if (!value) return undefined
    if (typeof value === 'object') return value as UploadErrorPayload
    if (typeof value !== 'string') return undefined
    try {
        return JSON.parse(value) as UploadErrorPayload
    } catch {
        return undefined
    }
}

type UploadOptions = {
    /** Fed the request's progress events, and started/stopped around it. */
    speed?: ReturnType<typeof useUploadSpeed>
    onProgress?: (event: ProgressEvent) => void
    signal?: AbortSignal
}

/**
 * POSTs `body` with XMLHttpRequest, since fetch can't report upload progress. Resolves with the parsed JSON response,
 * or rejects with an `UploadRequestError`.
 */
export function uploadWithProgress<T>(url: string, body: FormData, options: UploadOptions = {}): Promise<T> {
    const { speed, onProgress, signal } = options
    return new Promise<T>((resolve, reject) => {
        if (signal?.aborted) {
            reject(new UploadRequestError('aborted'))
            return
        }
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (event) => {
            speed?.progress(event)
            onProgress?.(event)
        })
        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText))
                } catch {
                    reject(new UploadRequestError('http', xhr.status))
                }
            } else {
                const payload = parseUploadErrorPayload(xhr.responseText)
                if (isMaintenanceRejection(xhr.status, payload)) window.dispatchEvent(new Event(MAINTENANCE_REJECTED_EVENT))
                reject(new UploadRequestError('http', xhr.status, payload))
            }
        })
        xhr.addEventListener('error', () => reject(new UploadRequestError('network')))
        xhr.addEventListener('abort', () => reject(new UploadRequestError('aborted')))
        xhr.addEventListener('loadend', () => {
            speed?.stop()
            signal?.removeEventListener('abort', abort)
        })

        const abort = () => xhr.abort()
        signal?.addEventListener('abort', abort, { once: true })

        xhr.open('POST', url)
        speed?.start()
        xhr.send(body)
    })
}
